import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Form,
} from 'reactstrap';
import { addDays, addWeeks } from 'date-fns';
import { Formik } from 'formik';
import { find, orderBy, lowerCase } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button, Field, useSession } from '@cordata/buffet';

import { authorizedInstance as axios } from '~/utils';

const ReminderModal = ({
  data,
  isOpen,
  action,
  toggle,
  onReassign,
  onSnooze,
  onDelete,
}) => {
  const { currentAppUser } = useSession();
  const [reassignOptions, setReassignOptions] = useState([]);

  useEffect(() => {
    if (currentAppUser && action === 'reassign') {
      axios
        .get('providers/mv', {
          params: {
            limit: 9999,
            org_users_only: true,
            'organization-id': currentAppUser.relationships.organizationUsers[0].attributes.orgUid,
            role_type: 'Navigator',
          },
        })
        .then(r => r.data.data)
        .then(r => r.map(rr => ({ ...rr, orderBy: lowerCase(rr.attributes.displayName) })))
        .then(r => orderBy(r, ['orderBy']))
        .then(r => setReassignOptions(r));
    }
  }, [currentAppUser, action]);

  const snoozeOptions = [{
    label: 'Tomorrow',
    value: addDays(new Date(), 1),
  }, {
    label: 'Next Week',
    value: addWeeks(new Date(), 1),
  }, {
    label: 'Customize',
    value: 'customize',
  }];

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      {action === 'reassign' && (
        <Formik
          initialValues={{
            assignTo: find(reassignOptions, ['attributes.providerUid', data.attributes.targetUid]),
          }}

          enableReinitialize // to ensure initialValues works

          onSubmit={(values) => {
            onReassign(values.assignTo);
          }}
        >
          {({
            handleSubmit,
          }) => (
            <Form noValidate onSubmit={f => f.preventDefault()}>
              <ModalHeader toggle={toggle}>
                Reassign reminder
              </ModalHeader>

              <ModalBody>
                <Field
                  name="assignTo"
                  type="select"
                  options={reassignOptions}
                  getOptionValue={o => o.attributes.providerUid}
                  getOptionLabel={o => o.attributes.displayName}
                />

                <span className="font-italic text-tertiary">Leave this blank to unassign this reminder</span>
              </ModalBody>

              <ModalFooter>
                <Button type="cancel" onClick={toggle}>
                  Cancel
                </Button>

                <Button color="primary" onClick={handleSubmit}>
                  <FontAwesomeIcon icon="user-edit" /> Reassign
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      )}

      {action === 'snooze' && (
        <Formik
          initialValues={{
            snoozeUntil: snoozeOptions[0],
            customDate: new Date(),
          }}

          onSubmit={(values) => {
            onSnooze(values.snoozeUntil.value === 'customize' ? values.customDate : values.snoozeUntil.value);
          }}
        >
          {({
            values,
            handleSubmit,
          }) => (
            <Form noValidate onSubmit={f => f.preventDefault()}>
              <ModalHeader toggle={toggle}>
                Snooze reminder
              </ModalHeader>

              <ModalBody>
                {values.snoozeUntil.value !== 'customize' ? (
                  <Field
                    name="snoozeUntil"
                    type="select"
                    options={snoozeOptions}
                  />
                ) : (
                  <Field
                    label="Snooze Until"
                    name="customDate"
                    type="date"
                  />
                )}
              </ModalBody>

              <ModalFooter>
                <Button type="cancel" onClick={toggle}>
                  Cancel
                </Button>

                <Button color="primary" onClick={handleSubmit}>
                  <FontAwesomeIcon icon="clock" /> Snooze
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      )}

      {action === 'delete' && (<>
        <ModalHeader toggle={toggle}>
          Delete reminder?
        </ModalHeader>

        <ModalBody>
          This action cannot be undone.
        </ModalBody>

        <ModalFooter>
          <Button type="cancel" onClick={toggle}>
            Cancel
          </Button>

          <Button color="danger" onClick={onDelete}>
            <FontAwesomeIcon icon="trash" /> Delete
          </Button>
        </ModalFooter>
      </>)}
    </Modal>
  );
};

ReminderModal.propTypes = {
  data: PropTypes.shape().isRequired,
  isOpen: PropTypes.bool,
  action: PropTypes.string.isRequired,
  toggle: PropTypes.func.isRequired,
  onReassign: PropTypes.func.isRequired,
  onSnooze: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

ReminderModal.defaultProps = {
  isOpen: false,
};

export default ReminderModal;
